import { ChevronDown, X } from 'lucide-react';
import { useId, useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { CONCERN_OPTIONS, type ConcernKey } from '../../constants/concerns';

interface Props<T extends string> {
  value: T[];
  onChange: (next: T[]) => void;
  // Defaults to the concern vocabulary; coping types pass their own.
  options?: readonly { value: T; label: string }[];
  clearLabel?: string;
  disabled?: boolean;
  // When true, render a small "* required" hint above the trigger (used in the
  // post-session completion dialog where >= 1 concern is enforced).
  required?: boolean;
  placeholder?: string;
  // The trigger is keyed off this id so the parent can `aria-labelledby` it.
  id?: string;
}

export function ConcernsMultiSelect<T extends string = ConcernKey>({
  value,
  onChange,
  options = CONCERN_OPTIONS as unknown as readonly { value: T; label: string }[],
  clearLabel = 'Clear concerns',
  disabled,
  required,
  placeholder = 'Select concerns',
  id: idProp,
}: Props<T>) {
  const generatedId = useId();
  const triggerId = idProp ?? generatedId;
  const [open, setOpen] = useState(false);

  const selected = new Set(value);

  function toggle(key: T) {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(options.map((o) => o.value).filter((v) => next.has(v)));
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange([]);
  }

  return (
    <div className="flex flex-col gap-1.5">
      {required ? <p className="text-meta-sm text-neutral-500">Pick at least one</p> : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={triggerId}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn(
              'text-body-md bg-surface flex w-full items-center justify-between gap-2 rounded-md border border-(--color-border) px-4 py-2.5 text-left transition-colors',
              'focus:border-primary-300 outline-none focus:shadow-(--shadow-focus-ring)',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {value.length === 0 ? (
                <span className="text-neutral-400">{placeholder}</span>
              ) : (
                value.map((c) => (
                  <span
                    key={c}
                    className="text-meta-sm bg-primary-50 text-accent-ink rounded-full border border-(--color-border) px-2.5 py-0.5"
                  >
                    {options.find((o) => o.value === c)?.label ?? c}
                  </span>
                ))
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {value.length > 0 && !disabled ? (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={clearLabel}
                  onClick={clear}
                  className="rounded-sm p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </span>
              ) : null}
              <ChevronDown className="h-4 w-4 text-neutral-500" aria-hidden />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[var(--radix-popover-trigger-width)] p-1"
        >
          <ul
            role="listbox"
            aria-multiselectable="true"
            onWheel={(e) => {
              // Dialog's scroll-lock intercepts wheel at the document level
              // (@radix-ui/react-remove-scroll). Stop propagation so the list
              // actually receives the wheel event. Mirrors TimePicker's Column.
              e.stopPropagation();
            }}
            className="pointer-events-auto flex max-h-64 flex-col overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300/70 [&::-webkit-scrollbar-track]:bg-transparent"
          >
            {options.map((opt) => {
              const checked = selected.has(opt.value);
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={checked}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm transition-colors',
                    'hover:bg-primary-50/60',
                  )}
                  onClick={() => {
                    toggle(opt.value);
                  }}
                >
                  <Checkbox checked={checked} aria-hidden tabIndex={-1} />
                  <span className="text-body-sm text-accent-ink">{opt.label}</span>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
